import React from "react";
// Direct module imports, not the controls barrel: this file is exported FROM the
// barrel, so importing it back would make a cycle.
import Select from "./Select";
import { sellableUnitOptions } from "../utils/cartLine";

/**
 * Reusable unit selector for line items (POS, transfers, adjustments, etc.)
 * Shows a dropdown of available units for a product (base unit + configured product units).
 *
 * Props:
 * - prod: the product object (must have baseUnitId, baseUnit, productUnits)
 * - value: currently selected unitId
 * - onChange: (unitId, conversionFactor, customPrice) => void
 * - filterFor: "sale" | "purchase" | undefined (filters by isForSale / isForPurchase)
 * - disabled: boolean
 * - size: "small" | "medium"
 * - sx: style overrides merged over the defaults below (a caller that attaches the
 *       select flush to a price box, for instance, squares the shared corner)
 * - fullWidth: default true (fills a grid cell); false sizes it to its own width, for a
 *       select sitting inline beside other fields
 *
 * Degrades to a plain unit label when the product offers no real choice, so it is
 * safe to render unconditionally in every row of a grid.
 */
const UnitSelect = ({ prod, value, onChange, filterFor, disabled, size = "small", sx, fullWidth = true }) => {
  // One list, shared with the POS cart dropdown and the unit picker dialog, so a
  // unit offered in one place is always offered in the others.
  const options = sellableUnitOptions(prod, filterFor);

  // No units configured, or only the base unit once the filter is applied — that
  // is not a choice, so show the unit as a plain label rather than a dead select.
  if (options.length <= 1)
    return (
      <span className="text-[12px] text-gray-400">
        {prod?.baseUnit?.abbreviation || prod?.baseUnit?.name || "—"}
      </span>
    );

  const handleChange = (e) => {
    const unitId = e.target.value;
    const sel = options.find((o) => o.id === unitId);
    if (onChange && sel) {
      onChange(unitId, sel.conversionFactor, sel.customPrice);
    }
  };

  return (
    <Select
      name="unitId"
      value={value || prod.baseUnitId}
      onChange={handleChange}
      options={options}
      variant="outlined"
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{
        "& .MuiInputBase-root": {
          height: size === "small" ? 32 : 40,
          fontSize: 13,
          borderRadius: "6px",
        },
        "& .MuiSelect-select": { py: "3px", px: "8px", fontSize: 12 },
        minWidth: 100,
        ...sx,
      }}
    />
  );
};

export default UnitSelect;
